// Pure helpers for reconciling an in-memory board with a board re-read from
// disk after a sync event. Extracted from TaskBoardTab so they are unit-testable
// and so the "when is a re-read a visible change?" rules live in one place.

// Order-independent signature of a board's drag-mutable arrangement: each task's
// (groupId, position) and each group's position, keyed by id. Two boards with an
// equal key have identical card/column placement — used to tell whether a drag
// actually moved anything (a drag that returns a card to its origin nets no
// change, even though updateBoard mints a fresh object each frame). Only fields a
// drag can touch are included; titles/done/priority never change during a drag.
export function boardArrangementKey(board) {
  if (!board) return '';
  const tasks = (board.tasks || [])
    .map((t) => `${t.id}:${t.groupId}:${t.position}`)
    .sort()
    .join('|');
  const groups = (board.groups || [])
    .map((g) => `${g.id}:${g.position}`)
    .sort()
    .join('|');
  return `${groups}#${tasks}`;
}

// Order-independent signature of a board's *content* (everything a re-read could
// change that boardArrangementKey doesn't cover): per-task id+updatedAt, per-group
// id/name/color, and the board's own scalar fields. Together with the arrangement
// key, equal keys mean a disk re-read holds nothing the user could see — so the
// repaint can be skipped entirely (our own sync echo).
//
// INVARIANT this key depends on: every task *content* mutation must stamp that
// task's `updatedAt` (today only handleChangeTask in TaskBoardTab does content
// edits, and it stamps). Drag moves change only groupId/position, which the
// arrangement key covers. If you add a new task-mutating path, stamp updatedAt
// or this key will falsely report "unchanged" and the re-read will be skipped.
// Tasks WITHOUT updatedAt (legacy boards imported before stamping existed) are
// exempt from the invariant: their entry is the full record, so any change is
// still detected — just via a heavier comparison.
export function boardContentKey(board) {
  if (!board) return '';
  const tasks = (board.tasks || [])
    .map((t) => (t.updatedAt == null ? JSON.stringify(t) : JSON.stringify([t.id, t.updatedAt])))
    .sort()
    .join('|');
  const groups = (board.groups || [])
    .map((g) => JSON.stringify([g.id, g.name, g.color]))
    .sort()
    .join('|');
  const scalars = JSON.stringify([
    board.name,
    board.description,
    board.color,
    board.viewMode,
    board.showDoneTasks,
    board.showColumnProgress,
  ]);
  return `${scalars}#${groups}#${tasks}`;
}

// Keep the in-memory task object when the loaded copy is equivalent on every
// field the UI can distinguish: content (updatedAt, per the invariant above) and
// placement (groupId, position). Preserving object identity lets React leave the
// card's DOM untouched — no remount, no scroll jump, no focus loss.
function keepTaskIfEqual(cur, loaded) {
  if (!cur) return loaded;
  // Legacy tasks (imported boards) may lack updatedAt — the fingerprint is
  // blind there, so fall back to comparing the whole record. Both objects come
  // from the same decompose/assemble shape, so stringify equality is reliable.
  if (cur.updatedAt == null || loaded.updatedAt == null) {
    return JSON.stringify(cur) === JSON.stringify(loaded) ? cur : loaded;
  }
  return cur.updatedAt === loaded.updatedAt &&
    cur.groupId === loaded.groupId &&
    cur.position === loaded.position
    ? cur
    : loaded;
}

function keepGroupIfEqual(cur, loaded) {
  if (!cur) return loaded;
  return cur.name === loaded.name &&
    cur.color === loaded.color &&
    cur.position === loaded.position
    ? cur
    : loaded;
}

// The two signatures are pure functions of a board object, and boards are
// immutable — every edit mints a fresh object (updateBoard is copy-on-write) —
// so a WeakMap keyed on object identity can never go stale and needs no manual
// invalidation. This spares recomputing the CURRENT board's fingerprints on
// every sync echo (the common own-echo case, where `current` is the same object
// as the previous echo). Entries are GC'd when the board object is dropped.
const boardKeyCache = new WeakMap();
function boardKeys(board) {
  let keys = boardKeyCache.get(board);
  if (!keys) {
    keys = { arrangement: boardArrangementKey(board), content: boardContentKey(board) };
    boardKeyCache.set(board, keys);
  }
  return keys;
}

/**
 * Reconcile the current in-memory board with a board re-read from disk.
 *
 * Returns `current` (same reference) when the loaded copy is indistinguishable
 * from what's already rendered — the caller must treat that as "skip setBoard".
 * Otherwise returns the loaded board with object identity preserved for every
 * task/group that didn't change, so React reconciliation only touches the
 * records that actually differ.
 *
 * The loaded record *set* is authoritative (this only runs when the board has
 * no unsaved local edits — the caller's dirty guard): remote-added records
 * appear, remote-deleted records drop out.
 */
export function reconcileBoards(current, loaded) {
  if (!loaded) return loaded;
  if (!current || current.id !== loaded.id) return loaded;
  // Memoize only `current` — the one board with reuse across echoes. `loaded` is
  // a fresh read every time (and reconcileBoards returns a NEW object when it
  // differs), so caching its keys would never pay off; compute them directly.
  // This also keeps boardKeys reachable only with the guaranteed-non-null
  // `current`, matching the null-tolerance of the key fns it wraps.
  const cur = boardKeys(current);
  if (
    cur.arrangement === boardArrangementKey(loaded) &&
    cur.content === boardContentKey(loaded)
  ) {
    return current;
  }
  const curTasks = new Map((current.tasks || []).map((t) => [t.id, t]));
  const curGroups = new Map((current.groups || []).map((g) => [g.id, g]));
  return {
    ...loaded,
    tasks: (loaded.tasks || []).map((t) => keepTaskIfEqual(curTasks.get(t.id), t)),
    groups: (loaded.groups || []).map((g) => keepGroupIfEqual(curGroups.get(g.id), g)),
  };
}
